// The rest of `JournalPosts` (SuiteModel/Journal.swift): which items are posts, the next post's number and the
// numbering's problems. `JournalPosts.number(in:)` lives with the typed writes (edit/editProject.ts).
import type { MetadataValue } from './contract.ts';
import { JournalPosts } from './edit/editProject.ts';
import { trimAll } from './publication.ts';

export {
  convertingNodes, demotedPosts, journalPosts, journalPostsInTree, movingNodes, POST_ONLY_FIELDS, postContaining, sectionPlacement, type JournalPostRef,
} from './model/posts.ts';

/** `nextNumber(after:from:)`: one more than the highest any document holds, never below the journal's first number. */
export function nextPostNumber(metadata: Iterable<Record<string, MetadataValue> | undefined>, first: number | null = null): number {
  let highest = 0;
  for (const m of metadata) { const n = JournalPosts.number(m); if (n !== null && n > highest) highest = n; }
  return Math.max(highest + 1, first ?? 1);
}

/** `JournalPosts.start(opening:signOff:)`: what New Post writes — the opening, two empty lines, then the sign-off, each as
 *  stored (placeholders kept), the caret (Unicode scalars) on the first empty line; null when both are empty. */
export function postStart(opening: string | null, signOff: string | null): { text: string; caret: number } | null {
  const o = trimAll(opening ?? ''), s = trimAll(signOff ?? '');
  if (o === '' && s === '') return null;
  return { text: o + '\n\n\n\n' + (s === '' ? '' : '\n' + s), caret: [...o].length + 2 };
}

/** `JournalPosts.hasOwnMatter`: the post carries its own opening and sign-off (`StandardField.ownPostMatter`). */
export function hasOwnPostMatter(metadata: Record<string, MetadataValue> | undefined): boolean {
  const v = metadata?.['own-post-matter'];
  return v?.type === 'boolean' && v.value === true;
}

/** `numberProblems`: numbers two posts share, and the gaps between the lowest and highest. */
export function postNumberProblems(numbers: number[]): { shared: number[]; missing: number[] } {
  const counts = new Map<number, number>();
  for (const n of numbers) counts.set(n, (counts.get(n) ?? 0) + 1);
  const shared = [...counts].filter(([, c]) => c > 1).map(([n]) => n).sort((a, b) => a - b);
  if (!counts.size) return { shared, missing: [] };
  const keys = [...counts.keys()];
  const low = Math.min(...keys), high = Math.max(...keys);
  if (high - low >= 10_000) return { shared, missing: [] };
  const missing: number[] = [];
  for (let n = low; n <= high; n++) if (!counts.has(n)) missing.push(n);
  return { shared, missing };
}

/** `ProjectDocument.firstPostNumber()`: the stored first number when it's above 1 and below the limit. */
export function firstPostNumberOf(settings: Record<string, unknown>): number | null {
  const n = settings.firstPostNumber;
  return typeof n === 'number' && Number.isInteger(n) && n > 1 && n < JournalPosts.numberLimit ? n : null;
}
